import React, { useState, useEffect, useCallback } from 'react';
import { 
  Autocomplete, 
  TextField, 
  Box, 
  Typography, 
  Chip, 
  CircularProgress,
  ListItemText,
  ListItem
} from '@mui/material';
import { Person, Work, Phone, Email } from '@mui/icons-material';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  employee_id: string;
  department: string;
  phone_number: string;
  email: string;
  site_roles: Array<{
    role: string;
    role_display: string;
    permissions: any;
  }>;
  can_receive_permits: boolean;
  can_supervise_work: boolean;
  can_receive_all_permit_types: boolean;
  can_receive_general_work_only: boolean;
}

interface UserSearchAutocompleteProps {
  siteId: number | null;
  roleFilter: 'recipients' | 'supervisors' | 'all';
  value: number | null;
  onChange: (userId: number | null) => void;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  permitType?: string;
}

const UserSearchAutocomplete: React.FC<UserSearchAutocompleteProps> = ({
  siteId,
  roleFilter,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  helperText,
  permitType
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Fetch users from API
  const fetchUsers = useCallback(async (search: string = '') => {
    if (!siteId) {
      setUsers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        role_filter: roleFilter,
        ...(search && { search })
      });

      const response = await fetch(`/api/v1/sites/sites/${siteId}/users/?${params}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Filter users based on permit type compatibility
      let filteredUsers = data.users || [];
      
      if (permitType && roleFilter === 'recipients') {
        filteredUsers = filteredUsers.filter((user: User) => {
          if (permitType === 'general_work') {
            // Both recipients and supervisors can receive general work
            return user.can_receive_permits;
          } else {
            // Only recipients can receive other permit types (not supervisors)
            return user.can_receive_all_permit_types;
          }
        });
      }
      
      setUsers(filteredUsers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, roleFilter, permitType]);

  // Initial fetch when component mounts or siteId changes
  useEffect(() => {
    fetchUsers(searchTerm);
  }, [fetchUsers, searchTerm]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
  }, []);

  // Handle user selection
  const handleUserChange = useCallback((event: any, newValue: User | null) => {
    onChange(newValue ? newValue.id : null);
  }, [onChange]);

  // Find selected user
  const selectedUser = users.find(user => user.id === value) || null;

  // Custom option rendering
  const renderOption = (props: any, option: User) => (
    <ListItem {...props} key={option.id}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Person sx={{ mr: 2, color: 'text.secondary' }} />
        <Box sx={{ flexGrow: 1 }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" component="span">
                  {option.full_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="span">
                  ({option.employee_id})
                </Typography>
              </Box>
            }
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Work sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {option.department}
                  </Typography>
                </Box>
                {option.phone_number && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {option.phone_number}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {option.site_roles.map((role, index) => (
                    <Chip
                      key={index}
                      label={role.role_display}
                      size="small"
                      variant="outlined"
                      color={role.role === 'recipient' ? 'primary' : 'secondary'}
                    />
                  ))}
                </Box>
              </Box>
            }
          />
        </Box>
      </Box>
    </ListItem>
  );

  // Custom input rendering
  const renderInput = (params: any) => (
    <TextField
      {...params}
      label={label}
      placeholder={placeholder}
      required={required}
      error={!!error}
      helperText={error || helperText}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <Person sx={{ color: 'text.secondary', mr: 1 }} />
        ),
        endAdornment: (
          <>
            {loading && <CircularProgress color="inherit" size={20} />}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
      onChange={handleSearchChange}
    />
  );

  return (
    <Autocomplete
      options={users}
      value={selectedUser}
      onChange={handleUserChange}
      getOptionLabel={(option) => option.full_name || ''}
      renderOption={renderOption}
      renderInput={renderInput}
      loading={loading}
      disabled={disabled || !siteId}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x} // We handle filtering server-side
      noOptionsText={
        !siteId ? 'Please select a site first' : 
        loading ? 'Loading...' : 
        searchTerm ? `No users found matching "${searchTerm}"` : 
        'No users found'
      }
      sx={{ minWidth: 300 }}
    />
  );
};

export default UserSearchAutocomplete; 